import {Pipe, PipeTransform} from '@angular/core';

/**
 * Normalisiert KI-generierten und vom Nutzer geschriebenen Freitext für die Anzeige über
 * `<markdown [data]="...">`: wandelt Aufzählungszeichen (•, ◦, –) in Markdown-Listensyntax um,
 * fügt vor Listen/nach einer "Quellen:"-Überschrift die für Markdown nötigen Leerzeilen bzw.
 * `-`-Präfixe ein, und **erhält einzelne Zeilenumbrüche** (siehe {@link keepSingleLineBreaks}).
 *
 * Der Text kommt aus einem Textfeld (Nutzerantwort) oder von einem Modell — beide setzen
 * Zeilenumbrüche als Gliederung ein. Markdown wertet einen einzelnen Umbruch aber nicht als
 * Zeilenwechsel, sondern klebt die Zeilen zu einem Absatz zusammen; im Bericht stand die
 * mehrzeilige Antwort dadurch als eine Zeile.
 */
export function normalizeMarkdown(input?: string | null): string {
  const normalized = (input ?? '')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/^\s*•\s+/gm, '- ')
    .replace(/^\s*◦\s+/gm, '  - ')
    .replace(/^\s*–\s+/gm, '- ');

  const lines = normalized.split('\n');
  const output: string[] = [];
  let inSourcesBlock = false;
  let inFence = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const isBlank = trimmed.length === 0;

    // Code-Blöcke wörtlich durchreichen – auch eine Zeile mit `|` darin ist dort keine Tabelle.
    if (/^(```|~~~)/.test(trimmed)) {
      inFence = !inFence;
      output.push(line);
      continue;
    }
    if (inFence) {
      output.push(line);
      continue;
    }

    // Tabellen sind ein eigener Block: davor und danach eine Leerzeile. Klebt die Tabelle am
    // Satz davor, hängt der Absatz sonst je nach Parser die Kopfzeile an seinen Text.
    const previousForTable = (output[output.length - 1] ?? '').trim();
    if (isTableLine(trimmed)) {
      if (previousForTable.length > 0 && !isTableLine(previousForTable)) {
        output.push('');
      }
      output.push(line);
      continue;
    }
    if (!isBlank && isTableLine(previousForTable)) {
      output.push('');
    }
    const isListItem = /^[-*+]\s+/.test(trimmed);
    const isSourcesHeader = /^quellen(\s*\/\s*kern)?/i.test(trimmed) && /:$/i.test(trimmed);

    if (isSourcesHeader) {
      inSourcesBlock = true;
      output.push(line);
      continue;
    }

    if (isBlank) {
      inSourcesBlock = false;
      output.push(line);
      continue;
    }

    if (inSourcesBlock && !isListItem) {
      output.push(`- ${trimmed}`);
      continue;
    }

    const previousLine = output[output.length - 1] ?? '';
    const previousTrimmed = previousLine.trim();
    const previousIsListItem = /^[-*+]\s+/.test(previousTrimmed);

    if (isListItem && previousTrimmed.length > 0 && !previousIsListItem) {
      output.push('');
    }

    // Und umgekehrt: eine Textzeile direkt nach einer Liste gehört NICHT mehr zur Liste. Markdown
    // würde sie als „lazy continuation" in den letzten Listenpunkt ziehen — im Bericht stand der
    // Folgesatz dann eingerückt im Aufzählungspunkt.
    if (!isListItem && previousIsListItem) {
      output.push('');
    }

    output.push(line);
  }

  return keepSingleLineBreaks(output).join('\n');
}

/**
 * Markiert einzelne Zeilenumbrüche als harte Umbrüche (zwei Leerzeichen am Zeilenende).
 *
 * Markdown braucht dafür entweder eine Leerzeile (= neuer Absatz) oder genau diese zwei
 * Leerzeichen. Ohne sie wird aus
 *
 * ```
 * Die Position ist unvollständig.
 * Fehlende Angabe: Menge.
 * ```
 *
 * ein einziger Absatz — die Gliederung, die der Verfasser gesetzt hat, ist im Bericht weg.
 *
 * Ausgenommen bleiben Code-Blöcke (dort ist der Inhalt wörtlich zu zeigen) und Zeilen, die schon
 * einen harten Umbruch tragen. Die letzte Zeile eines Absatzes bekommt keinen — vor einer Leerzeile
 * bzw. am Textende wäre er wirkungslos und würde nur unsichtbaren Leerraum anhängen.
 */
/** Zeile einer Markdown-Tabelle: beginnt mit `|` (Kopf, Trennzeile, Daten). */
function isTableLine(trimmed: string): boolean {
  return trimmed.startsWith('|') && trimmed.length > 1;
}

function keepSingleLineBreaks(lines: string[]): string[] {
  const fence = /^\s*(```|~~~)/;
  let inFence = false;
  return lines.map((line, index) => {
    if (fence.test(line.trim())) {
      inFence = !inFence;
      return line;
    }
    if (inFence) return line;
    const next = lines[index + 1];
    if (next === undefined || next.trim().length === 0) return line;
    const trimmed = line.trim();
    if (!trimmed) return line;
    // Listenpunkte und Überschriften sind eigene Blöcke; zwei Leerzeichen wären dort wirkungslos
    // und würden nur unsichtbaren Leerraum in den Text schreiben. Dasselbe gilt für die Zeile VOR
    // einer Liste — die Liste beginnt ohnehin einen neuen Block.
    const isListLine = (value: string) => /^([-*+]|\d+[.)])\s+/.test(value.trim());
    if (isListLine(trimmed) || trimmed.startsWith('#') || isListLine(next)) return line;
    // In einer Tabelle wäre ein harter Umbruch Teil der letzten Zelle.
    if (isTableLine(trimmed) || isTableLine(next.trim())) return line;
    if (/(\s{2}|\\)$/.test(line)) return line;
    return `${line}  `;
  });
}

@Pipe({
  name: 'normalizeMarkdown',
})
export class NormalizeMarkdownPipe implements PipeTransform {
  transform(value?: string | null): string {
    return normalizeMarkdown(value);
  }
}
